import { FC } from 'react';
import { Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LinkIcon from '@mui/icons-material/Link';
import LaunchIcon from '@mui/icons-material/Launch';
import DownloadIcon from '@mui/icons-material/Download';
import { IEntityContentField } from '@/lib/interfaces/Common';
import paths from '@/lib/consts/paths';

type SecretContentProps = {
	field: IEntityContentField;
};

enum ContentType {
	FILE = 'file',
	LINK = 'link',
}

const SecretContent: FC<SecretContentProps> = ({ field }) => {
	const navigate = useNavigate();
	const handleDownload = async () => {
		if (!field.content) {
			navigate(paths.plans);
		}
		try {
			// Fetch the file content
			const response = await fetch(field.content);

			if (!response.ok) {
				throw new Error('Failed to fetch the file.');
			}

			const blob = await response.blob();

			// Create a Blob URL
			const url = window.URL.createObjectURL(blob);

			// Ensure the file name ends with .json
			const jsonFileName = field.content.endsWith('.json')
				? field.content
				: `${field.content}.json`;
			const parts = jsonFileName.split('/');
			const lastPart = parts[parts.length - 1];

			// Create an anchor element
			const anchor = document.createElement('a');
			anchor.href = url;
			anchor.download = lastPart;

			// Trigger the download
			document.body.appendChild(anchor);
			anchor.click();

			// Clean up
			document.body.removeChild(anchor);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error downloading the file:', error);
		}
	};
	const handleOpenInNewTab = () => {
		if (!field.content) {
			navigate(paths.plans);
		}
		window.open(field.content, '_blank', 'noopener,noreferrer');
	};
	return (
		<div>
			{field.options?.type === ContentType.FILE && (
				<div>
					<Button
						color="primary"
						startIcon={<InsertDriveFileIcon color="secondary" />}
						endIcon={<DownloadIcon color="secondary" />}
						onClick={handleDownload}
						variant="outlined"
					>
						{field.options?.fieldName}
					</Button>
				</div>
			)}
			{field.options?.type === ContentType.LINK && (
				<div>
					<Button
						color="primary"
						startIcon={<LinkIcon color="secondary" />}
						endIcon={<LaunchIcon color="secondary" />}
						onClick={handleOpenInNewTab}
						variant="outlined"
					>
						{field.options?.fieldName}
					</Button>
				</div>
			)}
			{!field.content && (
				<Typography variant="caption">
					This content is accessible only to users with an active paid subscription.
				</Typography>
			)}
		</div>
	);
};

export default SecretContent;
